import { runtimeConfig } from '@/config/runtimeConfig';
import { SupabaseProvider } from './supabase/SupabaseProvider';
import { commit, db } from '@/data/localDb';
import { mockPi } from '@/data/mockPi';
import { mockReader } from '@/data/mockReader';
import { normalizeCardUid } from '@/lib/cards';
import { clubDateISO, nowISO } from '@/lib/dates';
import { AppError } from '@/lib/errors';
import { uid } from '@/lib/id';
import type {
  ActivityEvent,
  AppUser,
  AttendanceRecord,
  Booking,
  CardRole,
  CardTap,
  Club,
  Customer,
  Expense,
  FoodOrder,
  LightState,
  Member,
  MembershipPlan,
  MenuItem,
  Paise,
  Payment,
  PiStatus,
  RateWindow,
  ReaderStatus,
  Receptionist,
  Session,
  Station,
  StationType,
  WalletTransaction,
} from '@/types/domain';

export interface FoodOrderFilter {
  sessionId?: string | null;
}

/**
 * A rate "window"/band as the admin UI edits it: one time range carrying a
 * per-minute rate for each station type. On mock this becomes one RateWindow row
 * per type; on the live backend it is a single rate_windows row.
 */
export interface RateBandInput {
  clubId: string;
  label: string;
  startMinute: number;
  endMinute: number;
  rates: Record<StationType, Paise>;
}

export interface WalletMutationResult {
  member: Member;
  transaction: WalletTransaction;
}

/**
 * The single data seam. Every domain service depends on this interface and
 * nothing else. Swapping mock ⇄ supabase is a one-line change below — no
 * service, hook, or component is aware which implementation is live.
 */
export interface DataProvider {
  // clubs
  listClubs(): Promise<Club[]>;
  getClub(id: string): Promise<Club | null>;

  // users
  listUsers(clubId: string): Promise<AppUser[]>;
  findUserByEmail(email: string): Promise<AppUser | null>;
  updateUser(id: string, patch: Partial<AppUser>): Promise<AppUser>;

  // stations
  listStations(clubId: string): Promise<Station[]>;
  getStation(id: string): Promise<Station | null>;
  updateStation(id: string, patch: Partial<Station>): Promise<Station>;

  // rate windows
  listRateWindows(clubId: string): Promise<RateWindow[]>;
  createRateWindow(input: Omit<RateWindow, 'id'>): Promise<RateWindow>;
  /** Create a whole band (a time range with a rate for every station type). */
  createRateWindowBand(input: RateBandInput): Promise<void>;
  updateRateWindow(id: string, patch: Partial<RateWindow>): Promise<RateWindow>;
  deleteRateWindow(id: string): Promise<void>;

  // sessions
  listSessions(clubId: string): Promise<Session[]>;
  getSession(id: string): Promise<Session | null>;
  createSession(input: Omit<Session, 'id'>): Promise<Session>;
  updateSession(id: string, patch: Partial<Session>): Promise<Session>;

  // menu
  listMenuItems(clubId: string): Promise<MenuItem[]>;
  createMenuItem(input: Omit<MenuItem, 'id'>): Promise<MenuItem>;
  updateMenuItem(id: string, patch: Partial<MenuItem>): Promise<MenuItem>;

  // food orders
  listFoodOrders(clubId: string, filter?: FoodOrderFilter): Promise<FoodOrder[]>;
  createFoodOrder(input: Omit<FoodOrder, 'id'>): Promise<FoodOrder>;
  updateFoodOrder(id: string, patch: Partial<FoodOrder>): Promise<FoodOrder>;

  // bookings
  listBookings(clubId: string): Promise<Booking[]>;
  createBooking(input: Omit<Booking, 'id'>): Promise<Booking>;
  /**
   * Create a booking from the public (unauthenticated) site. Unlike
   * createBooking it never reads the row back, so it works for the anon role,
   * which may INSERT a booking but must never SELECT the bookings book (PII).
   */
  createBookingPublic(input: Omit<Booking, 'id'>): Promise<Booking>;
  updateBooking(id: string, patch: Partial<Booking>): Promise<Booking>;

  // customers & payments
  listCustomers(clubId: string): Promise<Customer[]>;
  listPayments(clubId: string): Promise<Payment[]>;
  createPayment(input: Omit<Payment, 'id'>): Promise<Payment>;

  // expenses
  listExpenses(clubId: string): Promise<Expense[]>;
  createExpense(input: Omit<Expense, 'id'>): Promise<Expense>;

  // activity
  listActivity(clubId: string, limit?: number): Promise<ActivityEvent[]>;
  addActivity(input: Omit<ActivityEvent, 'id'>): Promise<ActivityEvent>;

  // auth
  signIn(login: string, password: string): Promise<AppUser>;
  signOut(): Promise<void>;
  /** Self-service sign-up — always creates an inactive receptionist (STAFF) account. */
  signUp(input: { name: string; email: string; password: string }): Promise<void>;
  /** Email a password-reset link. `login` may be an email or a username. */
  requestPasswordReset(login: string): Promise<void>;
  /** Set a new password for the current recovery/authenticated session. */
  updatePassword(newPassword: string): Promise<void>;
  /** Fire `ready` once a password-recovery session is active (reset-link landing). Returns unsubscribe. */
  onAuthRecovery(ready: () => void): () => void;

  // members & wallet
  listMembers(clubId: string): Promise<Member[]>;
  getMember(id: string): Promise<Member | null>;
  findMemberByCardUid(clubId: string, cardUid: string): Promise<Member | null>;
  createMember(input: Omit<Member, 'id'>): Promise<Member>;
  updateMember(id: string, patch: Partial<Member>): Promise<Member>;
  /** Add an RFID tag to a member. A UID may belong to only one member. */
  linkCard(memberId: string, cardUid: string, label?: string, role?: CardRole): Promise<Member>;
  /** Remove one tag (by UID) from a member. */
  unlinkCard(memberId: string, cardUid: string): Promise<Member>;
  topUpWallet(memberId: string, amountPaise: number, note: string): Promise<WalletMutationResult>;
  debitWallet(
    memberId: string,
    amountPaise: number,
    note: string,
    ref?: { sessionId?: string | null; paymentId?: string | null },
  ): Promise<WalletMutationResult>;
  listWalletTransactions(clubId: string, memberId?: string): Promise<WalletTransaction[]>;

  // membership plans
  listMembershipPlans(clubId: string): Promise<MembershipPlan[]>;

  // attendance (reception desk roster + clock in/out log)
  listReceptionists(clubId: string): Promise<Receptionist[]>;
  addReceptionist(clubId: string, name: string): Promise<Receptionist>;
  removeReceptionist(id: string): Promise<void>;
  listAttendance(clubId: string): Promise<AttendanceRecord[]>;
  /** Open a shift for a receptionist. Throws if one is already open. */
  clockIn(clubId: string, receptionistId: string): Promise<AttendanceRecord>;
  /** Close the given open shift. */
  clockOut(recordId: string): Promise<AttendanceRecord>;

  // rfid reader (esp32 + rc522)
  getReaderStatus(): Promise<ReaderStatus>;
  waitForCardTap(timeoutMs: number, signal?: AbortSignal): Promise<CardTap>;
  simulateCardTap(cardUid: string): Promise<CardTap>;

  // pi lighting
  getPiStatus(): Promise<PiStatus>;
  setLight(stationId: string, on: boolean): Promise<LightState>;
}

/** Simulate a little network latency so loading states are exercised. */
function settle<T>(value: T, ms = 60): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

function patchInPlace<T extends { id: string }>(
  list: T[],
  id: string,
  patch: Partial<T>,
  label: string,
): T {
  const item = list.find((x) => x.id === id);
  if (!item) throw new AppError(`${label} not found`, 'NOT_FOUND');
  Object.assign(item, patch);
  commit();
  return clone(item);
}

class MockDataProvider implements DataProvider {
  async listClubs() {
    return settle(clone(db().clubs));
  }
  async getClub(id: string) {
    return settle(clone(db().clubs.find((c) => c.id === id) ?? null));
  }

  async listUsers(clubId: string) {
    return settle(clone(db().users.filter((u) => u.clubId === clubId)));
  }
  async findUserByEmail(email: string) {
    const found = db().users.find((u) => u.email.toLowerCase() === email.toLowerCase());
    return settle(clone(found ?? null));
  }
  async updateUser(id: string, patch: Partial<AppUser>) {
    return settle(patchInPlace(db().users, id, patch, 'User'));
  }

  async listStations(clubId: string) {
    const list = db()
      .stations.filter((s) => s.clubId === clubId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    return settle(clone(list));
  }
  async getStation(id: string) {
    return settle(clone(db().stations.find((s) => s.id === id) ?? null));
  }
  async updateStation(id: string, patch: Partial<Station>) {
    return settle(patchInPlace(db().stations, id, patch, 'Station'));
  }

  async listRateWindows(clubId: string) {
    return settle(clone(db().rateWindows.filter((r) => r.clubId === clubId)));
  }
  async createRateWindow(input: Omit<RateWindow, 'id'>) {
    const item: RateWindow = { ...input, id: uid('rw') };
    db().rateWindows.push(item);
    commit();
    return settle(clone(item));
  }
  async createRateWindowBand(input: RateBandInput) {
    const data = db();
    (Object.keys(input.rates) as StationType[]).forEach((type) => {
      data.rateWindows.push({
        id: uid('rw'),
        clubId: input.clubId,
        stationType: type,
        label: input.label,
        startMinute: input.startMinute,
        endMinute: input.endMinute,
        ratePerMinutePaise: input.rates[type],
        daysOfWeek: [],
        active: true,
      });
    });
    commit();
    return settle(undefined);
  }
  async updateRateWindow(id: string, patch: Partial<RateWindow>) {
    return settle(patchInPlace(db().rateWindows, id, patch, 'Rate window'));
  }
  async deleteRateWindow(id: string) {
    const data = db();
    data.rateWindows = data.rateWindows.filter((r) => r.id !== id);
    commit();
    return settle(undefined);
  }

  async listSessions(clubId: string) {
    return settle(clone(db().sessions.filter((s) => s.clubId === clubId)));
  }
  async getSession(id: string) {
    return settle(clone(db().sessions.find((s) => s.id === id) ?? null));
  }
  async createSession(input: Omit<Session, 'id'>) {
    const item: Session = { ...input, id: uid('sess') };
    db().sessions.push(item);
    commit();
    return settle(clone(item));
  }
  async updateSession(id: string, patch: Partial<Session>) {
    return settle(patchInPlace(db().sessions, id, patch, 'Session'));
  }

  async listMenuItems(clubId: string) {
    return settle(clone(db().menuItems.filter((m) => m.clubId === clubId)));
  }
  async createMenuItem(input: Omit<MenuItem, 'id'>) {
    const item: MenuItem = { ...input, id: uid('m') };
    db().menuItems.push(item);
    commit();
    return settle(clone(item));
  }
  async updateMenuItem(id: string, patch: Partial<MenuItem>) {
    return settle(patchInPlace(db().menuItems, id, patch, 'Menu item'));
  }

  async listFoodOrders(clubId: string, filter?: FoodOrderFilter) {
    let list = db().foodOrders.filter((o) => o.clubId === clubId);
    if (filter && 'sessionId' in filter) {
      list = list.filter((o) => o.sessionId === filter.sessionId);
    }
    return settle(clone(list));
  }
  async createFoodOrder(input: Omit<FoodOrder, 'id'>) {
    const item: FoodOrder = { ...input, id: uid('fo') };
    db().foodOrders.push(item);
    commit();
    return settle(clone(item));
  }
  async updateFoodOrder(id: string, patch: Partial<FoodOrder>) {
    return settle(patchInPlace(db().foodOrders, id, patch, 'Food order'));
  }

  async listBookings(clubId: string) {
    return settle(clone(db().bookings.filter((b) => b.clubId === clubId)));
  }
  async createBooking(input: Omit<Booking, 'id'>) {
    const item: Booking = { ...input, id: uid('bk'), createdBy: input.createdBy ?? 'counter' };
    db().bookings.push(item);
    commit();
    return settle(clone(item));
  }
  async createBookingPublic(input: Omit<Booking, 'id'>) {
    // In mock mode there is no RLS; mark it as a public (no operator) booking.
    const item: Booking = { ...input, id: uid('bk'), createdBy: null };
    db().bookings.push(item);
    commit();
    return settle(clone(item));
  }
  async updateBooking(id: string, patch: Partial<Booking>) {
    return settle(patchInPlace(db().bookings, id, patch, 'Booking'));
  }

  async listCustomers(clubId: string) {
    return settle(clone(db().customers.filter((c) => c.clubId === clubId)));
  }
  async listPayments(clubId: string) {
    return settle(clone(db().payments.filter((p) => p.clubId === clubId)));
  }
  async createPayment(input: Omit<Payment, 'id'>) {
    const item: Payment = { ...input, id: uid('pay') };
    db().payments.push(item);
    commit();
    return settle(clone(item));
  }

  async listExpenses(clubId: string) {
    return settle(clone(db().expenses.filter((e) => e.clubId === clubId)));
  }
  async createExpense(input: Omit<Expense, 'id'>) {
    const item: Expense = { ...input, id: uid('exp') };
    db().expenses.unshift(item);
    commit();
    return settle(clone(item));
  }

  async listActivity(clubId: string, limit = 20) {
    const list = db()
      .activity.filter((a) => a.clubId === clubId)
      .sort((a, b) => (a.at < b.at ? 1 : -1))
      .slice(0, limit);
    return settle(clone(list));
  }
  async addActivity(input: Omit<ActivityEvent, 'id'>) {
    const item: ActivityEvent = { ...input, id: uid('act') };
    db().activity.unshift(item);
    commit();
    return settle(clone(item));
  }

  async signIn(login: string, _password: string) {
    const user = await this.findUserByEmail(login);
    if (!user) throw new AppError('No account found for that email.', 'NOT_FOUND');
    if (user.status === 'PENDING') throw new AppError('Your account is awaiting admin approval.', 'FORBIDDEN');
    if (user.status === 'DISABLED') throw new AppError('This account has been disabled.', 'FORBIDDEN');
    return settle(user);
  }
  async signOut() {
    return settle(undefined);
  }
  async signUp(input: { name: string; email: string; password: string }) {
    const email = input.email.trim();
    if (db().users.some((u) => u.email.toLowerCase() === email.toLowerCase())) {
      throw new AppError('An account with that email already exists.', 'CONFLICT');
    }
    const user: AppUser = {
      id: uid('u'),
      clubId: db().clubs[0]?.id ?? 'high-eight',
      name: input.name.trim(),
      email,
      role: 'STAFF',
      status: 'PENDING',
      initials: input.name.trim().slice(0, 2).toUpperCase(),
      createdAt: new Date().toISOString(),
    };
    db().users.push(user);
    commit();
    return settle(undefined);
  }
  async requestPasswordReset(_login: string) {
    return settle(undefined);
  }
  async updatePassword(_newPassword: string) {
    return settle(undefined);
  }
  onAuthRecovery(ready: () => void) {
    // Mock mode has no email link — show the reset form immediately for local dev.
    ready();
    return () => {};
  }

  async listMembers(clubId: string) {
    return settle(clone(db().members.filter((m) => m.clubId === clubId)));
  }
  async getMember(id: string) {
    return settle(clone(db().members.find((m) => m.id === id) ?? null));
  }
  async findMemberByCardUid(clubId: string, cardUid: string) {
    const target = normalizeCardUid(cardUid);
    const found = db().members.find(
      (m) => m.clubId === clubId && m.cards.some((c) => normalizeCardUid(c.uid) === target),
    );
    return settle(clone(found ?? null));
  }
  async createMember(input: Omit<Member, 'id'>) {
    const item: Member = { ...input, id: uid('mem') };
    db().members.push(item);
    commit();
    return settle(clone(item));
  }
  async updateMember(id: string, patch: Partial<Member>) {
    return settle(patchInPlace(db().members, id, patch, 'Member'));
  }
  async linkCard(memberId: string, cardUid: string, label = 'Card', role: CardRole = 'MEMBER') {
    const target = normalizeCardUid(cardUid);
    const data = db();
    const clash = data.members.find((m) =>
      m.cards.some((c) => normalizeCardUid(c.uid) === target),
    );
    if (clash) {
      const who = clash.id === memberId ? 'this member' : clash.name;
      throw new AppError(`Tag already linked to ${who}`, 'CONFLICT');
    }
    const member = data.members.find((m) => m.id === memberId);
    if (!member) throw new AppError('Member not found', 'NOT_FOUND');
    member.cards.push({ uid: target, label: label.trim() || 'Card', role, addedAt: nowISO() });
    commit();
    return settle(clone(member));
  }
  async unlinkCard(memberId: string, cardUid: string) {
    const target = normalizeCardUid(cardUid);
    const data = db();
    const member = data.members.find((m) => m.id === memberId);
    if (!member) throw new AppError('Member not found', 'NOT_FOUND');
    member.cards = member.cards.filter((c) => normalizeCardUid(c.uid) !== target);
    commit();
    return settle(clone(member));
  }
  async topUpWallet(memberId: string, amountPaise: number, note: string) {
    return settle(this.moveWallet(memberId, 'TOPUP', amountPaise, note));
  }
  async debitWallet(
    memberId: string,
    amountPaise: number,
    note: string,
    ref?: { sessionId?: string | null; paymentId?: string | null },
  ) {
    return settle(this.moveWallet(memberId, 'DEBIT', amountPaise, note, ref));
  }
  async listWalletTransactions(clubId: string, memberId?: string) {
    const list = db()
      .walletTransactions.filter(
        (t) => t.clubId === clubId && (!memberId || t.memberId === memberId),
      )
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    return settle(clone(list));
  }

  /** Atomic wallet movement (mock): balance-check, apply, record the txn. */
  private moveWallet(
    memberId: string,
    type: 'TOPUP' | 'DEBIT',
    amountPaise: number,
    note: string,
    ref?: { sessionId?: string | null; paymentId?: string | null },
  ): WalletMutationResult {
    if (amountPaise <= 0) throw new AppError('Amount must be greater than zero', 'VALIDATION');
    const data = db();
    const member = data.members.find((m) => m.id === memberId);
    if (!member) throw new AppError('Member not found', 'NOT_FOUND');

    if (type === 'DEBIT' && member.walletBalancePaise < amountPaise) {
      throw new AppError('Insufficient wallet balance', 'CONFLICT');
    }

    member.walletBalancePaise += type === 'TOPUP' ? amountPaise : -amountPaise;

    const transaction: WalletTransaction = {
      id: uid('wt'),
      clubId: member.clubId,
      memberId,
      type,
      amountPaise,
      balanceAfterPaise: member.walletBalancePaise,
      note,
      sessionId: ref?.sessionId ?? null,
      paymentId: ref?.paymentId ?? null,
      createdAt: nowISO(),
    };
    data.walletTransactions.unshift(transaction);
    commit();
    return { member: clone(member), transaction: clone(transaction) };
  }

  async listMembershipPlans(clubId: string) {
    return settle(clone(db().membershipPlans.filter((p) => p.clubId === clubId)));
  }

  async listReceptionists(clubId: string) {
    const list = db()
      .receptionists.filter((r) => r.clubId === clubId && r.active)
      .sort((a, b) => a.name.localeCompare(b.name));
    return settle(clone(list));
  }
  async addReceptionist(clubId: string, name: string) {
    const trimmed = name.trim();
    if (!trimmed) throw new AppError('Name is required', 'VALIDATION');
    const item: Receptionist = {
      id: uid('rec'),
      clubId,
      name: trimmed,
      active: true,
      createdAt: nowISO(),
    };
    db().receptionists.push(item);
    commit();
    return settle(clone(item));
  }
  async removeReceptionist(id: string) {
    // Soft-retire: keep the roster entry so historical shifts still resolve.
    patchInPlace(db().receptionists, id, { active: false }, 'Receptionist');
    return settle(undefined);
  }
  async listAttendance(clubId: string) {
    const list = db()
      .attendance.filter((a) => a.clubId === clubId)
      .sort((a, b) => (a.clockInAt < b.clockInAt ? 1 : -1));
    return settle(clone(list));
  }
  async clockIn(clubId: string, receptionistId: string) {
    const data = db();
    const person = data.receptionists.find((r) => r.id === receptionistId);
    if (!person) throw new AppError('Receptionist not found', 'NOT_FOUND');
    const open = data.attendance.find(
      (a) => a.receptionistId === receptionistId && a.clockOutAt === null,
    );
    if (open) throw new AppError(`${person.name} is already clocked in`, 'CONFLICT');
    const now = nowISO();
    const item: AttendanceRecord = {
      id: uid('att'),
      clubId,
      receptionistId,
      receptionistName: person.name,
      workDate: clubDateISO(new Date(now)),
      clockInAt: now,
      clockOutAt: null,
    };
    data.attendance.unshift(item);
    commit();
    return settle(clone(item));
  }
  async clockOut(recordId: string) {
    const rec = db().attendance.find((a) => a.id === recordId);
    if (!rec) throw new AppError('Attendance record not found', 'NOT_FOUND');
    if (rec.clockOutAt) throw new AppError('This shift is already closed', 'CONFLICT');
    return settle(patchInPlace(db().attendance, recordId, { clockOutAt: nowISO() }, 'Attendance record'));
  }

  async getReaderStatus() {
    return settle(mockReader.status());
  }
  async waitForCardTap(timeoutMs: number, signal?: AbortSignal) {
    return new Promise<CardTap>((resolve, reject) => {
      const cleanup = () => {
        off();
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
      };
      const off = mockReader.onTap((tap) => {
        cleanup();
        resolve(tap);
      });
      const timer = setTimeout(() => {
        cleanup();
        reject(new AppError('No card detected — tap again.', 'NOT_FOUND'));
      }, timeoutMs);
      const onAbort = () => {
        cleanup();
        reject(new AppError('Cancelled', 'CONFLICT'));
      };
      signal?.addEventListener('abort', onAbort);
    });
  }
  async simulateCardTap(cardUid: string) {
    return settle(mockReader.emit(cardUid), 20);
  }

  async getPiStatus() {
    return settle(mockPi.getStatus());
  }
  async setLight(stationId: string, on: boolean) {
    return settle(mockPi.setLight(stationId, on));
  }
}

export const provider: DataProvider =
  runtimeConfig.dataProvider === 'supabase'
    ? new SupabaseProvider()
    : new MockDataProvider();
